import { useMemo, useState } from 'react';
import type { Package } from '@salonomy/types';
import { Button, Field, Modal, Pill, Select } from '@salonomy/ui';
import { usePackages } from '../../data/packages.js';
import { useSellMembership } from '../../data/memberships.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { benefitLabel } from './membershipFormat.js';
import './till.css';

/**
 * WS-13 Feature 3b — SELL a RECURRING MEMBERSHIP at the till, beside SellPackageModal. The catalogue can
 * define `membership_recurring` packages, but the PREPAID sell route (`POST /v1/client-packages`) 422s that
 * kind — a membership is a connected-account Stripe subscription, a SEPARATE plane. So this form offers
 * ONLY the `membership_recurring` SKUs and POSTs them to `POST /v1/client-memberships` (via
 * useSellMembership), which the prepaid SellPackageModal never offers (it filters memberships OUT).
 *
 * THE STAGING POSTURE (honesty — ADR-0009): under the fake gateway the subscription is SYNTHETIC and INERT
 * — NO real charge is taken and `stripeSubscriptionId` is null. So the copy here promises "the membership
 * starts" WITHOUT implying money moved; the success toast says the membership is active, never that a charge
 * was taken. The returned membership may land in `incomplete` (the first charge hasn't confirmed) — that is
 * expected on the fake gateway and is surfaced on the client record, not papered over.
 *
 * Sell is idempotent on a per-intent key minted once on submit; the button is disabled while pending so a
 * double-tap can't create two subscriptions, and a 422/409 surfaces faithfully.
 */
export function SellMembershipModal({
  clientId,
  clientName,
  onSold,
  onClose,
}: {
  /** The basket's client. A membership is sold to a specific client (the API requires a clientId). */
  clientId: string | null;
  clientName?: string | null;
  /** Called after a successful sell so the till can note the action (no money is taken on the fake gateway). */
  onSold?: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const sell = useSellMembership();
  // Only active catalogue packages are offered.
  const { data: page, isLoading } = usePackages(true, 0);

  // SELLABLE here = the recurring-membership kind ONLY. The prepaid sell route 422s this kind, so the
  // membership SKUs live exclusively on this surface (and are filtered OUT of SellPackageModal).
  const memberships = useMemo<Package[]>(
    () => (page?.data ?? []).filter((p) => p.kind === 'membership_recurring'),
    [page],
  );

  const [packageId, setPackageId] = useState('');
  const selected = memberships.find((p) => p.id === packageId) ?? null;
  const noClient = clientId == null;
  const canSell = !noClient && selected != null;

  async function submit() {
    if (!canSell || !selected || !clientId) return;
    try {
      // Mint the idempotency key once per intent; the button is disabled while pending so a double-tap
      // can't create a second subscription, and the API is idempotent on this key.
      const cm = await sell.mutateAsync({
        clientId,
        packageId: selected.id,
        idempotencyKey: crypto.randomUUID(),
      });
      onSold?.();
      // Surface the inert posture honestly: no charge was taken on the fake gateway — the membership is
      // simply started. We never claim money moved.
      toast.success(
        cm.stripeSubscriptionId == null
          ? `${selected.name} membership started — no charge taken (test mode).`
          : `${selected.name} membership started.`,
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not start the membership');
    }
  }

  // The member benefit is carried on the package's item(s) (PackageItem.discountBps) and is FROZEN onto
  // the membership at sale. Here we only PREVIEW the catalogue's configured benefit (the first item that
  // carries one) — display-only; the server freezes the real figure and the till applies the real line.
  const benefit = benefitLabel(
    selected?.items.find((i) => i.discountBps != null)?.discountBps ?? null,
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Start membership"
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {!noClient ? (
            <Button onClick={submit} disabled={!canSell || sell.isPending}>
              {sell.isPending ? 'Starting…' : 'Start membership'}
            </Button>
          ) : null}
        </>
      }
    >
      {noClient ? (
        <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          A membership is sold to a specific client, but this checkout has no client on it. Add a
          client to the booking first, then start the membership.
        </div>
      ) : isLoading ? (
        <div className="muted" style={{ fontSize: 13.5 }}>
          Loading the catalogue…
        </div>
      ) : memberships.length === 0 ? (
        <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          There are no membership plans yet. A manager can create a recurring-membership package on
          the Packages page.
        </div>
      ) : (
        <>
          {clientName ? (
            <div style={{ marginBottom: 8 }}>
              <Pill tone="neutral">For {clientName}</Pill>
            </div>
          ) : null}

          <Field label="Membership">
            <Select value={packageId} onChange={(e) => setPackageId(e.target.value)}>
              <option value="">Choose a membership…</option>
              {memberships.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {money(p.pricePence)}/month
                </option>
              ))}
            </Select>
          </Field>

          {selected ? (
            <div className="till-voucher__found" style={{ marginTop: 4 }}>
              <div className="till__line till__line--total">
                <span>Monthly price</span>
                <span className="mono">{money(selected.pricePence)}/mo</span>
              </div>
              {benefit ? (
                <div className="till__line muted">
                  <span>Member benefit</span>
                  <span className="mono">{benefit}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}>
            A membership is a recurring subscription on the salon&rsquo;s connected account. In test
            mode it starts immediately with <strong>no charge taken</strong> — the client gets the
            member benefit, and you can end it any time, end-of-period, in one click from the client
            record.
          </div>
        </>
      )}
    </Modal>
  );
}
